import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getCurrencySymbol } from '../../lib/utils';

import { useUser } from '../../contexts/UserContext';
import SubmitButton from '../buttons/StyledButton';
import CountryField from '../fields/Country';
import FormGroup from '../fields/FormGroup';
import TextField from '../fields/Text';

import styles from '../../../styles/forms/form.module.css';

const propTypes = {
    api: PropTypes.shape({
        createIntent: PropTypes.func.isRequired,
        confirmIntent: PropTypes.func.isRequired,
    }).isRequired,
    amount: PropTypes.number,
    amountEditable: PropTypes.bool,
    currency: PropTypes.string,
    onAmountChange: PropTypes.func,
    onSuccess: PropTypes.func,
    className: PropTypes.string,
};

const StripeForm = ({
    api: paymentApi = null,
    amount = 0.0,
    amountEditable = false,
    currency = 'CAD',
    onAmountChange = null,
    onSuccess = null,
    className = null,
}) => {
    const user = useUser();
    const intl = useIntl();

    const stripe = useStripe();
    const elements = useElements();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [country, setCountry] = useState(user !== null ? user.country || null : null);
    const [postalCode, setPostalCode] = useState(user !== null ? user.postalCode || null : null);
    const onCountryChange = useCallback((value) => setCountry(value), [setCountry]);
    const onPostalCodeChange = useCallback((value) => setPostalCode(value), [setPostalCode]);

    const billingDetails = useMemo(
        () => ({
            name: user !== null ? user.name : null,
            email: user !== null ? user.email : null,
            address: {
                country,
                postal_code: postalCode,
            },
        }),
        [user, country, postalCode],
    );

    const onSubmit = useCallback(
        async (e) => {
            e.preventDefault();

            if (!stripe || !elements) {
                // Stripe.js has not loaded yet. Make sure to disable
                // form submission until Stripe.js has loaded.
                return;
            }

            setLoading(true);

            let intentResponse = null;
            try {
                intentResponse = await paymentApi.createIntent(billingDetails.address);
                if (intentResponse.error) {
                    throw intentResponse.error.message;
                }
            } catch (err) {
                if (err && err.status === 403) {
                    setError(
                        intl.formatMessage({
                            defaultMessage:
                                'Sorry, this item is not currently available for purchase.',
                            description: 'Error message',
                        }),
                    );
                } else {
                    setError(err.message);
                }
                setLoading(false);
                return;
            }

            const { client_secret: secret } = intentResponse;

            // Confirm the payment
            let paymentResult = null;
            try {
                paymentResult = await stripe.confirmCardPayment(secret, {
                    payment_method: {
                        card: elements.getElement(CardElement),
                        billing_details: billingDetails,
                    },
                });

                // Handle errors
                if (paymentResult.error) {
                    throw paymentResult.error.message;
                }
            } catch (err) {
                setError(err.message);
                setLoading(false);
                return;
            }

            if (paymentResult.paymentIntent.status === 'succeeded') {
                // The payment has been processed!
                // Show a success message to your customer
                // There's a risk of the customer closing the window before callback
                // execution. Set up a webhook or plugin to listen for the
                // payment_intent.succeeded event that handles any business critical
                // post-payment actions.

                const { user: updatedUser = null, purchase = null } =
                    await paymentApi.confirmIntent(paymentResult.paymentIntent);

                if (onSuccess !== null) {
                    onSuccess({
                        user: updatedUser,
                        purchase,
                    });
                }
                setLoading(false);
                return;
            }

            setLoading(false);
            setError('Hopefully this will never happen');
        },
        [amount, billingDetails, paymentApi, stripe, elements, onSuccess, setError, setLoading],
    );

    const currencySymbol = getCurrencySymbol(currency);
    const disabled = loading || !stripe || amount === 0;

    return (
        <form
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            action={null}
            method="post"
            onSubmit={onSubmit}
        >
            <div className={styles.fieldsGroup}>
                <FormGroup
                    label={intl.formatMessage({
                        defaultMessage: 'Amount to pay',
                        description: 'Amount placeholder',
                    })}
                    className={classNames([styles.formGroup, styles.amount])}
                >
                    <div className={styles.fieldGroup}>
                        {amountEditable ? (
                            <div className={styles.withoutTickets}>
                                <TextField
                                    name="amount"
                                    type="number"
                                    value={amount}
                                    min="1"
                                    step="1"
                                    className={styles.field}
                                    disabled={loading}
                                    placeholder={intl.formatMessage({
                                        defaultMessage: 'Amount',
                                        description: 'Amount placeholder',
                                    })}
                                    onChange={onAmountChange}
                                />
                            </div>
                        ) : (
                            amount
                        )}
                        <div className={styles.currency}>
                            {`${currencySymbol} ${currency.toUpperCase()}`}
                        </div>
                    </div>
                </FormGroup>
                <FormGroup
                    label={intl.formatMessage({
                        defaultMessage: 'Card information',
                        description: 'Card information label',
                    })}
                    className={classNames([styles.formGroup, styles.card])}
                >
                    <div className={classNames([styles.field, styles.cardField])}>
                        <CardElement
                            options={{
                                hidePostalCode: true,
                                style: {
                                    base: {
                                        fontFamily: 'Munken Sans, Helvetica, Arial, sans-serif',
                                        fontSize: '18px',
                                        fontWeight: 'normal',
                                        backgroundColor: '#0e4e89',
                                        color: '#FFF',
                                        lineHeight: '2.5em',
                                        '::placeholder': {
                                            color: 'rgba(255,255,255,0.6)',
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                </FormGroup>
                <div className={styles.location}>
                    <FormGroup
                        label={intl.formatMessage({
                            defaultMessage: 'Country',
                            description: 'Country label',
                        })}
                        className={classNames([styles.formGroup, styles.country])}
                    >
                        <CountryField
                            name="country"
                            value={country}
                            disabled={loading}
                            required
                            className={styles.field}
                            onChange={onCountryChange}
                        />
                    </FormGroup>
                    <FormGroup
                        label={intl.formatMessage({
                            defaultMessage: 'Postal code',
                            description: 'Postal code label',
                        })}
                        className={classNames([styles.formGroup, styles.postalCode])}
                    >
                        <TextField
                            name="postal_code"
                            type="text"
                            value={postalCode}
                            disabled={loading}
                            required
                            className={styles.field}
                            placeholder={intl.formatMessage({
                                defaultMessage: 'Postal code',
                                description: 'Postal code placeholder',
                            })}
                            onChange={onPostalCodeChange}
                        />
                    </FormGroup>
                </div>
            </div>
            {error !== null ? <p className={styles.errors}>{error}</p> : null}
            <div className={styles.buttonGroup}>
                <SubmitButton
                    className={styles.submitBtn}
                    type="submit"
                    disabled={disabled}
                    loading={loading}
                >
                    <FormattedMessage defaultMessage="Purchase" description="Button label" />
                </SubmitButton>
            </div>
        </form>
    );
};
StripeForm.propTypes = propTypes;

export default StripeForm;
